// Client rental business + fleet Legal Readiness: shared client helpers.
//
// Every status shown here is computed by the backend
// (backend/client_legal/readiness.py). The UI never derives legal readiness
// itself, never shows a percentage, and never lets colour carry meaning on
// its own: every pill has a text label.
import { api } from "./api";

export const LR = "/compliance/legal-readiness";
export const CC = "/admin/client-compliance";

export const lrGet = (path, config) => api.get(`${LR}${path}`, config).then((r) => r.data);
export const lrPost = (path, body, config) => api.post(`${LR}${path}`, body, config).then((r) => r.data);
export const lrPatch = (path, body, config) => api.patch(`${LR}${path}`, body, config).then((r) => r.data);
export const lrPut = (path, body) => api.put(`${LR}${path}`, body).then((r) => r.data);
export const ccGet = (path, config) => api.get(`${CC}${path}`, config).then((r) => r.data);
export const ccPost = (path, body) => api.post(`${CC}${path}`, body).then((r) => r.data);

export const COMPLIANCE_CAPS = [
  "compliance.view", "compliance.manage", "compliance.documents.view", "compliance.documents.upload",
  "compliance.vehicle.view", "compliance.vehicle.manage", "compliance.insurance.view",
  "compliance.insurance.manage", "compliance.export", "compliance.tax.view",
];

export const STATUS_LABELS = {
  NOT_STARTED: "Not started",
  FACTS_REQUIRED: "Setup answer needed",
  SELF_REPORTED: "Self-reported (not verified)",
  EVIDENCE_REQUIRED: "Evidence required",
  EVIDENCE_UPLOADED: "Evidence uploaded (not yet reviewed)",
  SUBMITTED_TO_AUTHORITY: "Submitted to the agency",
  UNDER_REVIEW: "Awaiting ConfirmPH review",
  VERIFIED: "Verified by ConfirmPH review",
  EXPIRING_SOON: "Verified, expiring soon",
  EXPIRED: "Expired",
  REJECTED: "Evidence not accepted",
  ACTION_REQUIRED: "Action required",
  REVIEW_REQUIRED: "Review required",
  NOT_APPLICABLE: "Not applicable",
  NEEDS_AUTHORITY_REVIEW: "Agency review recommended",
  LEGAL_INTERPRETATION_REQUIRED: "Professional review recommended",
  REGULARIZATION_REQUIRED: "Registration incomplete",
  LEGALLY_BLOCKED: "Legally blocked",
  // Business / vehicle final states
  SETUP_IN_PROGRESS: "Setup in progress",
  READY: "Ready",
  NOT_CONFIGURED: "Not configured",
  BLOCKED: "Blocked",
  READY_FOR_RENTAL: "Ready for rental",
  // Vehicle insurance / transport / safety
  CURRENT: "Current (reviewed)",
  EXPIRED_BLOCKED: "Expired (blocked)",
  EXPIRED_UNCONFIRMED: "Recorded as expired",
  NOT_VERIFIED: "Not verified",
  COVERAGE_REVIEW_REQUIRED: "Coverage review required",
  RENTAL_USE_CONFIRMED: "Rental use confirmed",
  NOT_PROVIDED: "Not provided",
  FRANCHISE_VERIFIED: "Authority verified",
  SELF_DRIVE_EXEMPTION_RECORDED: "Self-drive classification reviewed",
  NEEDS_REVIEW: "Needs review",
  SAFETY_HOLD: "Safety hold",
  OUT_OF_SERVICE: "Out of service",
  INSPECTION_DUE: "Inspection due",
  TASK_5_REQUIRED: "Driver system not yet available",
  DRIVER_SYSTEM_ACTIVE: "Checked per driver in Drivers",
  EVALUATED_PER_TRIP: "Checked for the assigned driver per trip",
};

export const LEVEL_LABELS = {
  NONE: "Nothing recorded",
  SELF_REPORTED: "Told ConfirmPH (self-reported)",
  LEGACY_RECORDED: "Older record (not reviewed)",
  EVIDENCE_UPLOADED: "Evidence uploaded",
  BUSINESS_REVIEWED: "Checked internally by your business",
  CONFIRMPH_VERIFIED: "ConfirmPH reviewed the evidence",
  AUTHORITY_CONFIRMED: "Issuing authority independently confirmed",
};

const GOOD = new Set(["VERIFIED", "READY", "READY_FOR_RENTAL", "CURRENT", "RENTAL_USE_CONFIRMED",
  "FRANCHISE_VERIFIED", "SELF_DRIVE_EXEMPTION_RECORDED"]);
const BAD = new Set(["EXPIRED", "LEGALLY_BLOCKED", "BLOCKED", "EXPIRED_BLOCKED", "REJECTED", "SAFETY_HOLD",
  "OUT_OF_SERVICE"]);
const REVIEW = new Set(["NEEDS_AUTHORITY_REVIEW", "REVIEW_REQUIRED", "COVERAGE_REVIEW_REQUIRED",
  "NEEDS_REVIEW", "LEGAL_INTERPRETATION_REQUIRED", "UNDER_REVIEW", "TASK_5_REQUIRED"]);
const NEUTRAL = new Set(["NOT_APPLICABLE"]);

export function statusTone(status) {
  if (GOOD.has(status)) return "border-emerald-200 bg-emerald-50 text-emerald-800";
  if (BAD.has(status)) return "border-rose-200 bg-rose-50 text-rose-700";
  if (REVIEW.has(status)) return "border-violet-200 bg-violet-50 text-violet-800";
  if (NEUTRAL.has(status)) return "border-slate-200 bg-slate-50 text-slate-600";
  if (status === "EXPIRING_SOON" || status === "INSPECTION_DUE") return "border-amber-200 bg-amber-50 text-amber-800";
  // Everything unknown or unverified reads as outstanding, never as good.
  return "border-amber-200 bg-amber-50 text-amber-800";
}

export function statusLabel(status) {
  return STATUS_LABELS[status] || String(status || "").replace(/_/g, " ").toLowerCase();
}

export function hasCap(caps, cap) {
  return (caps || []).includes(cap);
}

export function errorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return "Something went wrong. Please try again.";
}

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const CATEGORY_TABS = [
  { key: "entity", label: "Business" },
  { key: "local", label: "Local permits" },
  { key: "bir", label: "BIR & accounting" },
  { key: "transport", label: "Transport authority" },
  { key: "privacy", label: "Privacy" },
  { key: "employment", label: "Employment" },
];

export function humanise(value) {
  return String(value || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
